import { BlockType } from '@common/entities';

import { Block } from 'types/fields';

import { getFieldMappingForBlockType } from './get-block-field-mapping';

function getEnabledConfigurationFields(configuration: unknown): Array<{ name: string }> {
  if (!configuration || typeof configuration !== 'object') return [];

  return Object.entries(configuration).flatMap(([key, value]) => {
    if (key === 'fields' && Array.isArray(value)) {
      return value.filter(
        (field): field is { name: string; enabled?: boolean } =>
          Boolean(field?.name) && field.enabled !== false,
      );
    }
    if (Array.isArray(value)) return value.flatMap(getEnabledConfigurationFields);
    return getEnabledConfigurationFields(value);
  });
}

/**
 * Blocks that store their fields at the root level (no block-type prefix).
 * Extracted here so it can be shared between the display-logic registration
 * hook and any component that needs to resolve a block's form field names.
 */
export const ROOT_LEVEL_BLOCKS = new Set<BlockType>([
  BlockType.PersonalInformation,
  BlockType.AlternateName,
]);

/**
 * Returns the react-hook-form field name prefix for a block type.
 * Root-level blocks (PersonalInformation, AlternateName) register their
 * sub-fields without a prefix; all others use the block type as a prefix.
 */
export function getBlockFormFieldPrefix(blockType: BlockType): string {
  return ROOT_LEVEL_BLOCKS.has(blockType) ? '' : blockType;
}

/**
 * Returns the list of react-hook-form field names for all enabled fields in a block.
 * Handles both prefixed blocks (e.g. `EmergencyContact.contact_first_name`)
 * and root-level blocks (e.g. `applicant_name_first`).
 */
export function getBlockFormFieldNames(
  block: Pick<Block, 'customType' | 'configuration'>,
): string[] {
  const prefix = getBlockFormFieldPrefix(block.customType);
  const configurationFields = getEnabledConfigurationFields(block.configuration);
  const mappings = getFieldMappingForBlockType({
    blockType: block.customType,
    configuration: configurationFields.length
      ? { ...block.configuration, fields: configurationFields }
      : block.configuration,
    formFieldPrefix: prefix,
  });
  return Array.from(
    new Set<string>(
      mappings.map((mapping: { blockFieldFormFieldName: string }) =>
        String(mapping.blockFieldFormFieldName),
      ),
    ),
  );
}
